// Ranks the warehouse pickup locations on the product page by straight-line
// distance from a ZIP code the shopper enters, shows the closest five, and
// remembers the ZIP across product pages.
//
// The ZIP to coordinates lookup is served from theme assets: ten JSON shards
// (zip-centroids-0.json ... zip-centroids-9.json, split by the ZIP's first
// digit) built from the US Census Bureau ZCTA gazetteer. One shard is fetched
// per lookup and cached for the page. Each pickup row carries the Shopify
// location's latitude/longitude as data attributes, so no external service is
// involved.
class PickupLocationFinder {
  constructor(finder) {
    this.finder = finder;
    this.input = finder.querySelector('[data-pickup-zip-input]');
    this.submitButton = finder.querySelector('[data-pickup-zip-submit]');
    this.clearButton = finder.querySelector('[data-pickup-zip-clear]');
    this.message = finder.querySelector('[data-pickup-zip-message]');
    this.wrapper = document.getElementById('pickup-availability-wrapper');
    this.shardUrls = finder.dataset.zipCentroidAssets.split('|');
    this.shardCache = {};
    this.storageKey = 'hld-pickup-zip';
    this.closestCount = 5;
    this.minimumRowsForFinder = 3;
    this.activeZip = null;
    this.activeCoordinates = null;

    this.strings = {
      invalid: finder.dataset.textInvalid,
      notFound: finder.dataset.textNotFound,
      showing: finder.dataset.textShowing,
      showAll: finder.dataset.textShowAll,
      showFewer: finder.dataset.textShowFewer,
    };

    this.submitButton.addEventListener('click', () => this.lookup(this.input.value));
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.lookup(this.input.value);
      }
    });
    this.clearButton.addEventListener('click', () => this.clear());
    this.wrapper.addEventListener('pickup-availability:rendered', () => this.handleRender());

    const storedZip = window.localStorage.getItem(this.storageKey);
    if (storedZip) {
      this.input.value = storedZip;
    }
    this.handleRender();
  }

  get rows() {
    return Array.from(this.wrapper.querySelectorAll('.warehouse-pickup-info-location'));
  }

  // Runs on page load and after every variant change re-renders the list.
  async handleRender() {
    const rows = this.rows;
    this.finder.hidden = rows.length < this.minimumRowsForFinder;
    if (this.finder.hidden) return;

    rows.forEach((row, index) => {
      row.dataset.originalIndex = index;
    });

    const storedZip = window.localStorage.getItem(this.storageKey);
    if (storedZip) {
      await this.lookup(storedZip);
    }
  }

  async lookup(rawZip) {
    const zip = String(rawZip || '').trim();
    if (!/^\d{5}$/.test(zip)) {
      this.showMessage(this.strings.invalid, true);
      return;
    }

    const coordinates = await this.coordinatesForZip(zip);
    if (!coordinates) {
      this.showMessage(this.strings.notFound, true);
      return;
    }

    this.activeZip = zip;
    this.activeCoordinates = coordinates;
    window.localStorage.setItem(this.storageKey, zip);
    this.input.value = zip;
    this.clearButton.hidden = false;
    this.showMessage(this.strings.showing.replace('[zip]', zip), false);
    this.rankRows();
  }

  // The Census data covers ZIP Code Tabulation Areas, which leave out
  // PO-box-only ZIPs (30301 in downtown Atlanta, for example). Those share
  // their first four digits with the surrounding area, so the nearest ZIP
  // number with the same prefix stands in for them.
  async coordinatesForZip(zip) {
    const shardIndex = Number(zip[0]);
    if (!this.shardCache[shardIndex]) {
      const response = await fetch(this.shardUrls[shardIndex]);
      this.shardCache[shardIndex] = await response.json();
    }
    const shard = this.shardCache[shardIndex];
    if (shard[zip]) return shard[zip];

    const prefix = zip.slice(0, 4);
    const target = Number(zip);
    let nearestZip = null;
    Object.keys(shard).forEach((candidate) => {
      if (!candidate.startsWith(prefix)) return;
      if (nearestZip === null || Math.abs(Number(candidate) - target) < Math.abs(Number(nearestZip) - target)) {
        nearestZip = candidate;
      }
    });
    return nearestZip ? shard[nearestZip] : null;
  }

  rankRows() {
    const rows = this.rows;
    if (!rows.length) return;
    const list = rows[0].parentElement;
    const [zipLatitude, zipLongitude] = this.activeCoordinates;

    const ranked = rows
      .map((row) => ({
        row,
        distance: this.distanceInMiles(zipLatitude, zipLongitude, row.dataset.latitude, row.dataset.longitude),
      }))
      .sort((a, b) => a.distance - b.distance);

    ranked.forEach(({ row }, index) => {
      list.appendChild(row);
      row.classList.toggle('is-beyond-closest', index >= this.closestCount);
    });

    this.renderShowAllButton(list, rows.length);
  }

  renderShowAllButton(list, total) {
    const container = list.parentElement;
    let button = container.querySelector('[data-pickup-show-all]');
    if (total <= this.closestCount) {
      if (button) button.remove();
      container.classList.remove('is-expanded');
      return;
    }
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'warehouse-pickup-info-show-all';
      button.setAttribute('data-pickup-show-all', '');
      button.addEventListener('click', () => {
        container.classList.toggle('is-expanded');
        this.updateShowAllLabel(button, container, total);
      });
      container.appendChild(button);
    }
    this.updateShowAllLabel(button, container, total);
  }

  updateShowAllLabel(button, container, total) {
    button.textContent = container.classList.contains('is-expanded')
      ? this.strings.showFewer
      : this.strings.showAll.replace('[count]', total);
  }

  clear() {
    window.localStorage.removeItem(this.storageKey);
    this.activeZip = null;
    this.activeCoordinates = null;
    this.input.value = '';
    this.clearButton.hidden = true;
    this.message.hidden = true;

    const rows = this.rows;
    if (!rows.length) return;
    const list = rows[0].parentElement;
    rows
      .sort((a, b) => Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex))
      .forEach((row) => {
        row.classList.remove('is-beyond-closest');
        list.appendChild(row);
      });
    const container = list.parentElement;
    container.classList.remove('is-expanded');
    const button = container.querySelector('[data-pickup-show-all]');
    if (button) button.remove();
  }

  showMessage(text, isError) {
    this.message.textContent = text;
    this.message.hidden = false;
    this.message.classList.toggle('is-error', isError);
  }

  // Haversine great-circle distance. Rows without coordinates sort last.
  distanceInMiles(latitude1, longitude1, latitude2, longitude2) {
    if (latitude2 === '' || longitude2 === '') return Infinity;
    const toRadians = (degrees) => (Number(degrees) * Math.PI) / 180;
    const earthRadiusMiles = 3958.8;
    const deltaLatitude = toRadians(latitude2 - latitude1);
    const deltaLongitude = toRadians(longitude2 - longitude1);
    const a =
      Math.sin(deltaLatitude / 2) ** 2 +
      Math.cos(toRadians(latitude1)) * Math.cos(toRadians(latitude2)) * Math.sin(deltaLongitude / 2) ** 2;
    return 2 * earthRadiusMiles * Math.asin(Math.sqrt(a));
  }
}

document.querySelectorAll('[data-pickup-zip-finder]').forEach((finder) => new PickupLocationFinder(finder));
